import { useEffect, useMemo, useState } from "react";
import { X } from "lucide-react";
import HeroSection from "../components/HeroSection";
import FormatsSection from "../components/FormatsSection";
import WorkflowSuggestions from "../components/WorkflowSuggestions";
import type { Workflow } from "../components/WorkflowSuggestions";
import GlobalBackground from "../components/GlobalBackground";
import InputSection from "../components/InputSection";
import ConvertAction from "../components/convertAction";
import ProcessingUI from "../components/ProcessingUI";
import ActionButtons from "../components/ActionButtons";
import HowItWorks from "../components/HowItWorks";
import Footer from "../components/Footer";
import TopHeader from "../components/TopHeader";
import PopularMcps from "../components/PopularMcps";
import SampleSpecs from "../components/SampleSpecs";
import LiveStats from "../components/LiveStats";
import Testimonials from "../components/Testimonials";
import Reveal from "../components/Reveal";
import { trackConversion, trackError, trackSpecInput } from "../analytics";
import { getSubscriptionStatus } from "../lib/subscriptionApi";
import PreviewPage, { DEMO_DATA } from "../components/PreviewPage";
import ConnectPage from "../components/ConnectPage";
import { credentialsComplete, detectAuthType, type AuthScheme, type Credentials } from "../components/AuthConfigStep";
import { overrideToScheme, type OverrideMode } from "../components/AuthOverrideStep";
import {
  buildAuthSetupUiFlags,
  logAuthSetupSnapshot,
  logPreviewAuthResponse,
  type AuthSetupDebugSnapshot,
} from "../lib/authSetupDebug";

import { zeroEndpointMessage, type PreviewApiResponse } from "../lib/previewResponse";
import AuthDetectionBanner, { type AuthRequirement } from "../components/AuthDetectionBanner";
import { extractOAuthConfig, type OAuthProviderConfig } from "../components/OAuthConnectButton";
import {
  base64ToFile,
  fileToUploadIntent,
  readFileAsBase64,
  tryResumeAuthWorkflow,
  type ConvertHostIntent,
} from "../lib/authResume";
import {
  clearConversionWorkspace,
  loadConversionWorkspace,
  saveConversionWorkspace,
} from "../lib/conversionWorkspace";
import { requireSignedIn } from "../lib/requireSignedIn";
import { needsBaseUrlOverride } from "../lib/baseUrl";
import { recordUserConversion } from "../lib/userProfile";
import { claimReferral } from "../lib/subscriptionApi";
import { fetchSessionUser } from "../lib/sessionUser";
import {
  uploadOpenAPI,
  uploadOpenAPIFromUrl,
  uploadOpenAPIFromText,
  convertToMCP,
  convertToMCPFromUrl,
  convertToMCPFromText,
  downloadZipFromBase64,
  hostMCP,
  hostMCPFromUrl,
  hostMCPFromText,
  resolveHostedEndpoint,
  mergeSpecsFromUrls,
} from "../api";

type RawPreviewResponse = {
  detected_format?: string;
  is_grouped?: boolean;                     
  grouped_endpoints?: Array<{               
    group_name: string;
    resource: string;
    endpoint_count: number;
    endpoints: Array<{
      method?: string;
      path?: string;
      summary?: string;
      support_level?: string;
      issues?: Array<{ message?: string }>;
    }>;
  }> | null;
  zip_b64?: string;
  report?: {
    summary?: {
      schema_warnings_suppressed?: number;
      schema_warnings_low?: number;
      schema_warnings_medium?: number;
    };
  };
  spec?: {
    title?: string;
    base_url?: string;
    auth_schemes?: Array<{ type?: string; name?: string; scheme?: string }>;
  };
  endpoints?: Array<{
    method?: string;
    path?: string;
    summary?: string;
    support_level?: string;
    issues?: Array<{ type?: string; severity?: string; message?: string }>;
  }>;
  auth_requirement?: AuthRequirement;
  workflows?: Workflow[];
};

type HostMCPResponse = {
  ok?: boolean;
  mcp_url?: string;
  sse_url?: string;
  server_id?: string;
  spec?: { title?: string; base_url?: string };
  auth_requirement?: AuthRequirement;
};

function hostedPathsFromResponse(h: HostMCPResponse): { mcp: string; sse: string } {
  const id = h.server_id?.trim();
  const mcpPath = h.mcp_url?.trim() || (id ? `/mcp/${id}` : "");
  const ssePath = h.sse_url?.trim() || (id ? `/mcp/${id}/sse` : "");
  return {
    mcp: mcpPath ? resolveHostedEndpoint(mcpPath) : "",
    sse: ssePath ? resolveHostedEndpoint(ssePath) : "",
  };
}

function toErrorMessage(err: unknown, fallback: string): string {
  if (err && typeof err === "object" && "message" in err) {
    const msg = (err as { message?: unknown }).message;
    if (typeof msg === "string" && msg.trim()) return msg;
  }
  return fallback;
}

function formatAutoFixMessage(message: string): string {
  const m = message.toLowerCase();
  if (m.includes("operation_id") && m.includes("synthesized")) {
    return "generated operation id";
  }
  if (m.includes("description") && m.includes("auto-generated")) {
    return "generated missing description";
  }
  if (m.includes("defaulted to string") && m.includes("param")) {
    return "missing param type -> string";
  }
  if (m.includes("method missing") && m.includes("defaulted to get")) {
    return "missing method -> GET";
  }
  if (m.includes("inferred auth") || m.includes("auth") && m.includes("inferred")) {
    return "inferred auth settings";
  }
  return message;
}

function applyPreviewResult(
  data: PreviewApiResponse,
  setParsedRawData: (value: RawPreviewResponse) => void,
  setUploadError: (value: string | null) => void,
) {
  setParsedRawData(data as RawPreviewResponse);
  const zeroMsg = zeroEndpointMessage(data);
  if (zeroMsg) setUploadError(zeroMsg);
}

function toPreviewPageData(raw?: RawPreviewResponse) {
  if (!raw || !Array.isArray(raw.endpoints)) return DEMO_DATA;

  const endpoints = raw.endpoints.map((ep) => {
    const issue = ep.issues?.[0]?.message || "";
    const support = ep.support_level || "full";
    const status =
      support === "manual" ? "error" : support === "partial" ? "warning" : "ok";
    const autoFixes = (ep.issues || [])
      .filter((i) => i.type === "missing_field" || /synthesized|auto-generated|defaulted|inferred/i.test(i.message || ""))
      .map((i) => formatAutoFixMessage(i.message || ""))
      .slice(0, 3);

    return {
      method: ep.method || "GET",
      path: ep.path || "/",
      description: ep.summary || `${ep.method || "GET"} ${ep.path || "/"}`,
      status,
      issue,
      autoFixes,
    };
  });

  const firstAuth = raw.spec?.auth_schemes?.[0];
  const summary = raw.report?.summary;

  return {
    format: raw.detected_format || "Detected API",
    base_url: raw.spec?.base_url || "",
    is_grouped: raw.is_grouped || false,                    // ← add
    grouped_endpoints: raw.grouped_endpoints || null,       // ← add
    auth: firstAuth
      ? {
          type: firstAuth.type || firstAuth.scheme || "unknown",
          scheme: firstAuth.scheme,   // preserve e.g. "bearer" | "basic"
          header: firstAuth.name || "-",
        }
      : { type: "none", header: "-" },
    endpoints,
    schema_warning_counts: {
      suppressed: summary?.schema_warnings_suppressed ?? 0,
      low: summary?.schema_warnings_low ?? 0,
      medium: summary?.schema_warnings_medium ?? 0,
    },
  };
}

const Index = () => {
  const [sessionEmail, setSessionEmail] = useState<string | null>(null);
  const [isProPlan, setIsProPlan] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  /** Tracks which sample spec URL is currently being fetched (drives spinner on the card) */
  const [loadingSampleUrl, setLoadingSampleUrl] = useState<string | null>(null);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  /** When set, Generate / Convert uses this URL instead of uploadedFile */
  const [fetchedSpecUrl, setFetchedSpecUrl] = useState<string | null>(null);
  /** Raw spec text from Paste tab after successful preview — used for convert */
  const [pastedSpecText, setPastedSpecText] = useState<string | null>(null);
  const [isConverting, setIsConverting] = useState(false);
  const [lastConvertSuccess, setLastConvertSuccess] = useState(false);
  const [parsedRawData, setParsedRawData] = useState<RawPreviewResponse | null>(null);
  /** Shown in ConvertAction / ConnectPage — set immediately after host succeeds (not derived from connectSetup). */
  const [hostedMcpDisplayUrl, setHostedMcpDisplayUrl] = useState<string | null>(null);
  const [connectSetup, setConnectSetup] = useState<{
    specTitle: string;
    baseUrl: string;
    serverPath: string;
    mcpUrl: string;
    sseUrl: string;
  } | null>(null);

  const [credentials, setCredentials] = useState<Credentials>({});
  const [credentialsSkipped, setCredentialsSkipped] = useState(false);
  // OAuth escape hatch — when set, an OAuth-detected spec falls back to a
  // bearer-token credential field so the user isn't forced through OAuth.
  const [useApiKeyInsteadOfOAuth, setUseApiKeyInsteadOfOAuth] = useState(false);
  const [baseUrlOverride, setBaseUrlOverride] = useState("");
  /** Auth requirement returned by the backend after hosting — drives AuthDetectionBanner */
  const [detectedAuthRequirement, setDetectedAuthRequirement] = useState<AuthRequirement | null>(null);
  // Manual auth override — set by AuthOverrideStep when the spec didn't
  // declare a usable scheme (oauth2-only, openIdConnect, or missing).
  const [authOverride, setAuthOverride] = useState<OverrideMode | null>(null);

  const parsedData = useMemo(() => toPreviewPageData(parsedRawData || undefined), [parsedRawData]);
  const showBaseUrlInput = useMemo(
    () => needsBaseUrlOverride(parsedData.base_url),
    [parsedData.base_url],
  );
  const baseUrlReady = !showBaseUrlInput || !!baseUrlOverride.trim();

  // Extract auth schemes from parsed preview for the AuthConfigStep
  const authSchemes: AuthScheme[] = useMemo(() => {
    const raw = parsedRawData?.spec?.auth_schemes;
    if (!raw || !Array.isArray(raw)) return [];
    return raw.map((s: {
      type?: string;
      scheme?: string;
      name?: string;
      location?: string;
      is_complex?: boolean;
      authorization_url?: string | null;
      token_url?: string | null;
      scopes?: string[];
    }) => ({
      type:      s.type      || "",
      scheme:    s.scheme,
      name:      s.name,
      location:  s.location,
      is_complex: s.is_complex,
      authorization_url: s.authorization_url,
      token_url: s.token_url,
      scopes: s.scopes,
    }));
  }, [parsedRawData]);

  // Usable = anything that gives us a real input field (basic / bearer / apiKey).
  // We deliberately strip oauth2 / openIdConnect here so the downstream
  // AuthConfigStep doesn't fall back to its "OAuth not supported" amber note.
  // If everything was stripped, the manual override UI takes over.
  const usableSchemesFromSpec: AuthScheme[] = useMemo(() => {
    const usable = authSchemes.filter(s =>
      s.type === "http" || s.type === "apiKey"
    );

    if (usable.length > 0) return usable;

    // Fall back to whatever the preview summary inferred (covers Postman /
    // unstructured specs where backend skipped securitySchemes).
    const displayAuthType   = (parsedData.auth?.type   || "").toLowerCase();
    const displayAuthScheme = ((parsedData.auth as { scheme?: string })?.scheme || "").toLowerCase();
    if (displayAuthType === "http") return [{ type: "http", scheme: displayAuthScheme || "bearer" }];
    if (displayAuthType === "bearer") return [{ type: "http", scheme: "bearer" }];
    if (displayAuthType === "basic")  return [{ type: "http", scheme: "basic" }];
    if (displayAuthType === "apikey") return [{ type: "apiKey" }];

    return [];
  }, [authSchemes, parsedData.auth]);

  // When the spec only declared OAuth2 / OIDC (or nothing usable), we need
  // the user to pick a manual override before we have any input fields.
  const onlyComplexAuth =
    authSchemes.length > 0 &&
    authSchemes.every(s => s.type === "oauth2" || s.type === "openIdConnect");
  const oauthConfig: OAuthProviderConfig | null = useMemo(() => {
    const hasOauthScheme = authSchemes.some(
      s => s.type === "oauth2" || s.type === "openIdConnect",
    );
    if (!hasOauthScheme) return null;
    return extractOAuthConfig(authSchemes);
  }, [authSchemes]);

  const hasOAuthSupport =
    oauthConfig !== null && !!(oauthConfig.authorizeUrl && oauthConfig.tokenUrl);

  // Manual picker only when we cannot run OAuth from the spec URLs.
  const needsManualOverride = usableSchemesFromSpec.length === 0 && !hasOAuthSupport;
  const overrideReason: "oauth2" | "missing" = onlyComplexAuth ? "oauth2" : "missing";

  const oauthConnected = !!credentials.oauth_access_token_enc?.trim();

  // The final list of schemes used by AuthConfigStep + credentialsComplete.
  // Order of preference:
  //   1. Real usable schemes from the spec
  //   2. Manual override the user picked (synthesised into an AuthScheme)
  //   3. Empty (Generate stays disabled and the override UI is shown)
  const authSchemesForSetup: AuthScheme[] = useMemo(() => {
    if (usableSchemesFromSpec.length > 0) return usableSchemesFromSpec;
    if (authOverride) return overrideToScheme(authOverride);
    // OAuth-only spec where the user clicked "Use API key instead" — fall back
    // to a bearer-token input (most common alternative for APIs that also
    // accept long-lived tokens).
    if (hasOAuthSupport && useApiKeyInsteadOfOAuth) return [{ type: "http", scheme: "bearer" }];
    return [];
  }, [usableSchemesFromSpec, authOverride, hasOAuthSupport, useApiKeyInsteadOfOAuth]);

  // Generate is allowed when either we have a working scheme + credentials,
  // OR the user explicitly said "no auth needed".
  const authReady =
    oauthConnected ||
    credentialsSkipped ||
    authOverride === "none" ||
    (authSchemesForSetup.length > 0 && credentialsComplete(authSchemesForSetup, credentials)) ||
    (authSchemesForSetup.length === 0 && !needsManualOverride);

  const authDetectType = detectAuthType(authSchemesForSetup);
  const showSetupSection = Boolean(parsedRawData && !lastConvertSuccess);
  const authSetupUi = buildAuthSetupUiFlags({
    showSetupSection,
    showBaseUrlInput,
    needsManualOverride,
    authSchemesForSetup,
    detectAuthTypeResult: authDetectType,
  });

  const primaryOAuth = parsedRawData?.auth_requirement?.type === "oauth2";
  const staticCredentialOverride =
    authOverride === "bearer" || authOverride === "basic" || authOverride === "apiKey";
  // OAuth APIs use the banner skip link — hide duplicate "No API key" AuthConfigStep row,
  // unless the user clicked "Use API key instead" (in which case show the credential field).
  const showAuthConfigOnPage =
    authSetupUi.showAuthConfig &&
    (!primaryOAuth || staticCredentialOverride || useApiKeyInsteadOfOAuth);
  const showAuthOverrideOnPage = authSetupUi.showAuthOverride && !hasOAuthSupport;

  const authSetupDebugSnapshot = useMemo((): AuthSetupDebugSnapshot => ({
    ts: new Date().toISOString(),
    source: "render",
    parsedRawData: Boolean(parsedRawData),
    lastConvertSuccess,
    baseUrl: parsedData.base_url,
    showBaseUrlInput,
    baseUrlReady,
    authSchemesRaw: parsedRawData?.spec?.auth_schemes ?? null,
    authSchemes,
    parsedDataAuth: parsedData.auth,
    usableSchemesFromSpec,
    onlyComplexAuth,
    needsManualOverride,
    overrideReason,
    authOverride,
    authSchemesForSetup,
    detectAuthType: authDetectType,
    authReady,
    ui: authSetupUi,
    hideInlineSetup: true,
    setupSlotPassed: showSetupSection,
  }), [
    parsedRawData,
    lastConvertSuccess,
    parsedData.base_url,
    parsedData.auth,
    showBaseUrlInput,
    baseUrlReady,
    authSchemes,
    usableSchemesFromSpec,
    onlyComplexAuth,
    needsManualOverride,
    overrideReason,
    authOverride,
    authSchemesForSetup,
    authDetectType,
    authReady,
    authSetupUi,
    showSetupSection,
  ]);

  useEffect(() => {
    console.warn("[AuthSetup] bundle marker embed-auth-v2");
  }, []);

  // Restore converter state after navigation until user starts a new spec.
  useEffect(() => {
    const ws = loadConversionWorkspace();
    if (!ws) return;
    if (ws.parsedRawData) setParsedRawData(ws.parsedRawData as RawPreviewResponse);
    if (ws.fetchedSpecUrl) setFetchedSpecUrl(ws.fetchedSpecUrl);
    if (ws.pastedSpecText) setPastedSpecText(ws.pastedSpecText);
    if (ws.fileSnapshot) {
      setUploadedFile(
        base64ToFile(
          ws.fileSnapshot.fileName,
          ws.fileSnapshot.fileType,
          ws.fileSnapshot.dataBase64,
        ),
      );
    }
    setBaseUrlOverride(ws.baseUrlOverride || "");
    setCredentials(ws.credentials || {});
    setCredentialsSkipped(Boolean(ws.credentialsSkipped));
    setAuthOverride(ws.authOverride);
    if (ws.hostedMcpDisplayUrl) setHostedMcpDisplayUrl(ws.hostedMcpDisplayUrl);
    if (ws.connectSetup) setConnectSetup(ws.connectSetup);
    setLastConvertSuccess(Boolean(ws.lastConvertSuccess));
  }, []);

  useEffect(() => {
    if (!parsedRawData && !lastConvertSuccess) return;
    const timer = window.setTimeout(() => {
      void (async () => {
        let fileSnapshot: { fileName: string; fileType: string; dataBase64: string } | null = null;
        if (uploadedFile) {
          fileSnapshot = {
            fileName: uploadedFile.name,
            fileType: uploadedFile.type,
            dataBase64: await readFileAsBase64(uploadedFile),
          };
        }
        saveConversionWorkspace({
          parsedRawData,
          fetchedSpecUrl,
          pastedSpecText,
          fileSnapshot,
          baseUrlOverride,
          credentials,
          credentialsSkipped,
          authOverride,
          hostedMcpDisplayUrl,
          connectSetup,
          lastConvertSuccess,
        });
      })();
    }, 350);
    return () => window.clearTimeout(timer);
  }, [
    parsedRawData,
    fetchedSpecUrl,
    pastedSpecText,
    uploadedFile,
    baseUrlOverride,
    credentials,
    credentialsSkipped,
    authOverride,
    hostedMcpDisplayUrl,
    connectSetup,
    lastConvertSuccess,
  ]);

  useEffect(() => {
    if (!parsedRawData) return;
    logAuthSetupSnapshot("react-state", authSetupDebugSnapshot);
  }, [parsedRawData, authSetupDebugSnapshot]);

  const runFileUpload = async (file: File) => {
    trackSpecInput("upload", file.name.split(".").pop());
    clearConversionWorkspace();
    try {
      setUploadError(null);
      setIsUploading(true);
      setLastConvertSuccess(false);
      setParsedRawData(null);
      setConnectSetup(null);
      setHostedMcpDisplayUrl(null);
      setDetectedAuthRequirement(null);
      setUploadedFile(file);
      setFetchedSpecUrl(null);
      setPastedSpecText(null);
      setCredentials({});
      setCredentialsSkipped(false);
      setBaseUrlOverride("");
      setAuthOverride(null);
      setUseApiKeyInsteadOfOAuth(false);

      const data = await uploadOpenAPI(file);
      applyPreviewResult(data, setParsedRawData, setUploadError);
      logPreviewAuthResponse("file-upload-response", data);
    } catch (err: unknown) {
      setUploadError(toErrorMessage(err, "Upload failed"));
    } finally {
      setIsUploading(false);
    }
  };

  const runUrlFetch = async (url: string) => {
    if (!url.trim()) {
      setUploadError("Enter a URL to fetch.");
      return;
    }
    clearConversionWorkspace();
    try {
      setUploadError(null);
      setIsUploading(true);
      setLastConvertSuccess(false);
      setParsedRawData(null);
      setConnectSetup(null);
      setHostedMcpDisplayUrl(null);
      setUploadedFile(null);
      setPastedSpecText(null);
      setCredentials({});
      setCredentialsSkipped(false);
      setBaseUrlOverride("");
      setAuthOverride(null);
      setUseApiKeyInsteadOfOAuth(false);
      setDetectedAuthRequirement(null);
      const data = await uploadOpenAPIFromUrl(url.trim());
      setFetchedSpecUrl(url.trim());
      applyPreviewResult(data, setParsedRawData, setUploadError);
      logPreviewAuthResponse("url-fetch-response", data);
      trackSpecInput("url", undefined);
    } catch (err: unknown) {
      setFetchedSpecUrl(null);
      setUploadError(toErrorMessage(err, "Fetch failed"));
    } finally {
      setIsUploading(false);
    }
  };

  const runPastePreview = async (text: string) => {
    const t = text.replace(/^\uFEFF/, "").trim();
    if (!t) {
      setUploadError("Paste your OpenAPI or Swagger JSON/YAML, then try again.");
      return;
    }
    clearConversionWorkspace();
    try {
      setUploadError(null);
      setIsUploading(true);
      setLastConvertSuccess(false);
      setParsedRawData(null);
      setConnectSetup(null);
      setHostedMcpDisplayUrl(null);
      setUploadedFile(null);
      setFetchedSpecUrl(null);
      setCredentials({});
      setCredentialsSkipped(false);
      setBaseUrlOverride("");
      setAuthOverride(null);
      setUseApiKeyInsteadOfOAuth(false);
      setDetectedAuthRequirement(null);
      const data = await uploadOpenAPIFromText(t);
      setPastedSpecText(t);
      applyPreviewResult(data, setParsedRawData, setUploadError);
    } catch (err: unknown) {
      setPastedSpecText(null);
      setUploadError(toErrorMessage(err, "Parse failed"));
    } finally {
      setIsUploading(false);
    }
  };

  const runConvert = async (intent?: ConvertHostIntent) => {
    const credsFromIntent = intent?.credentials;
    if (credsFromIntent && Object.keys(credsFromIntent).length > 0) {
      setCredentials({
        username: credsFromIntent.username,
        password: credsFromIntent.password,
        api_key: credsFromIntent.api_key,
      });
    }
    const baseOverride =
      intent?.baseUrlOverride?.trim() || baseUrlOverride.trim() || undefined;

    const pasted = intent?.source === "paste" ? intent.text?.trim() : pastedSpecText;
    const fetchedUrl = intent?.source === "url" ? intent.url?.trim() : fetchedSpecUrl;
    const file =
      intent?.source === "file" && intent.fileName && intent.dataBase64
        ? base64ToFile(intent.fileName, intent.fileType ?? "", intent.dataBase64)
        : uploadedFile;

    if (!file && !fetchedUrl && !pasted) {
      setUploadError(
        "Upload a file, paste JSON/YAML and click Parse & preview, or fetch from URL before converting to MCP.",
      );
      return;
    }

    const effectiveBaseUrl = baseOverride || parsedData.base_url;
    if (needsBaseUrlOverride(effectiveBaseUrl)) {
      setUploadError(
        `Enter your real API base URL above${parsedData.base_url ? ` (the spec lists ${parsedData.base_url})` : ""} before generating.`,
      );
      return;
    }

    const effectiveCreds = credsFromIntent ?? credentials;
    if (hasOAuthSupport && !oauthConnected && !credentialsSkipped) {
      setUploadError("Connect with OAuth before generating.");
      return;
    }
    if (needsManualOverride && !authOverride) {
      setUploadError("Pick an authentication method above before generating.");
      return;
    }
    if (
      !credentialsSkipped &&
      authSchemesForSetup.length > 0 &&
      !credentialsComplete(authSchemesForSetup, effectiveCreds)
    ) {
      setUploadError("Fill in your API credentials above before generating.");
      return;
    }

    try {
      setUploadError(null);
      setLastConvertSuccess(false);
      setHostedMcpDisplayUrl(null);
      setIsConverting(true);

      if (intent?.source === "paste" && pasted) {
        setPastedSpecText(pasted);
        setFetchedSpecUrl(null);
        setUploadedFile(null);
      } else if (intent?.source === "url" && fetchedUrl) {
        setFetchedSpecUrl(fetchedUrl);
        setPastedSpecText(null);
        setUploadedFile(null);
      } else if (intent?.source === "file" && file) {
        setUploadedFile(file);
        setFetchedSpecUrl(null);
        setPastedSpecText(null);
      }

      const hostUser = await fetchSessionUser();
      const hostEmail = (hostUser?.email?.trim() || sessionEmail?.trim() || "").toLowerCase() || null;
      if (!hostEmail) {
        setUploadError("Sign in is required to save your MCP to your dashboard.");
        return;
      }

      // ── Plan enforcement: check conversion limit for free users ─────────
      if (hostEmail) {
        try {
          const sub = await getSubscriptionStatus(hostEmail);
          if (sub.plan === "free" && sub.conversions_left === 0) {
            throw new Error(
              "You've reached your 3 free conversions this month. Upgrade to Pro for unlimited conversions.",
            );
          }
        } catch (subErr: unknown) {
          const msg = subErr instanceof Error ? subErr.message : "";
          if (msg.includes("free conversions")) throw subErr;
          // If status check fails, allow conversion to proceed
        }
      }

      const hostOpts = {
        credentials:
          !credentialsSkipped && Object.keys(effectiveCreds).length > 0
            ? (effectiveCreds as Record<string, string>)
            : null,
        baseUrlOverride: baseOverride || null,
      };

      let hostData: HostMCPResponse;
      if (pasted) {
        hostData = (await hostMCPFromText(pasted, hostEmail, hostOpts)) as HostMCPResponse;
      } else if (fetchedUrl) {
        hostData = (await hostMCPFromUrl(fetchedUrl, { email: hostEmail, ...hostOpts })) as HostMCPResponse;
      } else if (file) {
        hostData = (await hostMCP(file, hostEmail, hostOpts)) as HostMCPResponse;
      } else {
        throw new Error("Nothing to convert.");
      }

      const { mcp: mcpUrlAbs, sse: sseUrlAbs } = hostedPathsFromResponse(hostData);
      if (mcpUrlAbs) setHostedMcpDisplayUrl(mcpUrlAbs);
      // Capture backend-detected auth requirement for the banner
      if (hostData.auth_requirement) {
        setDetectedAuthRequirement(hostData.auth_requirement);
      } else {
        setDetectedAuthRequirement(null);
      }

      let result: RawPreviewResponse | null = null;
      if (pasted) {
        result = await convertToMCPFromText(pasted);
      } else if (fetchedUrl) {
        result = await convertToMCPFromUrl(fetchedUrl);
      } else if (file) {
        result = await convertToMCP(file);
      }

      const zipB64 = (result as { zip_b64?: string } | null)?.zip_b64 || null;
      if (zipB64) {
        try {
          downloadZipFromBase64(zipB64, result?.spec?.title || "mcp-server");
          sessionStorage.setItem("latest_mcp_zip_b64", zipB64);
        } catch (zipErr: unknown) {
          setUploadError(toErrorMessage(zipErr, "ZIP download failed"));
        }
      }

      const connect = {
        specTitle: hostData.spec?.title || parsedData.format || "my-api",
        baseUrl: hostData.spec?.base_url || parsedData.base_url || "",
        serverPath: "server.py",
        mcpUrl: mcpUrlAbs,
        sseUrl: sseUrlAbs,
      };
      setConnectSetup(connect);
      setLastConvertSuccess(true);

      let fileSnapshot: { fileName: string; fileType: string; dataBase64: string } | null = null;
      if (file) {
        fileSnapshot = {
          fileName: file.name,
          fileType: file.type,
          dataBase64: await readFileAsBase64(file),
        };
      }
      saveConversionWorkspace({
        parsedRawData,
        fetchedSpecUrl,
        pastedSpecText: pasted,
        fileSnapshot,
        baseUrlOverride: baseOverride || baseUrlOverride,
        credentials: effectiveCreds as Credentials,
        credentialsSkipped,
        authOverride,
        hostedMcpDisplayUrl: mcpUrlAbs || null,
        connectSetup: connect,
        lastConvertSuccess: true,
        serverId: hostData.server_id?.trim() || null,
      });

      trackConversion({
        spec_title: hostData.spec?.title,
        tool_count: (hostData as { tool_count?: number }).tool_count,
        base_url: hostData.spec?.base_url,
      });
      void recordUserConversion().then(() => {
        window.dispatchEvent(new Event("user-profile-changed"));
        window.dispatchEvent(new Event("user-mcp-changed"));
      });
      // Claim referral if a ref code was captured on landing
      const pendingRef = localStorage.getItem("referral_ref_code");
      if (pendingRef && sessionEmail) {
        void claimReferral(sessionEmail, pendingRef).then(r => {
          if (r.claimed) localStorage.removeItem("referral_ref_code");
        });
      }
    } catch (err: unknown) {
      setLastConvertSuccess(false);
      const msg = toErrorMessage(err, "MCP hosting failed");
      setUploadError(msg);
      trackError(msg, "runConvert");
    } finally {
      setIsConverting(false);
    }
  };

  // Load session email for subscription checks + MCP registration
  useEffect(() => {
    fetch("/api/auth/session")
      .then(r => r.ok ? r.json() : null)
      .then((s: { user?: { email?: string } } | null) => {
        const e = s?.user?.email?.trim().toLowerCase() || null;
        setSessionEmail(e);
        if (e) {
          getSubscriptionStatus(e).then(sub => setIsProPlan(sub.plan === "pro")).catch(() => {});
        }
      })
      .catch(() => {});
    const onUpdate = () => {
      fetch("/api/auth/session")
        .then(r => r.ok ? r.json() : null)
        .then((s: { user?: { email?: string } } | null) => {
          const e = s?.user?.email?.trim().toLowerCase() || null;
          setSessionEmail(e);
          if (e) {
            getSubscriptionStatus(e).then(sub => setIsProPlan(sub.plan === "pro")).catch(() => {});
          }
        })
        .catch(() => {});
    };
    window.addEventListener("auth-session-updated", onUpdate);
    return () => window.removeEventListener("auth-session-updated", onUpdate);
  }, []);

  // Capture referral code from ?ref= URL param and persist to localStorage
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get("ref");
    if (ref && ref.trim()) {
      localStorage.setItem("referral_ref_code", ref.trim());
    }
  }, []);

  useEffect(() => {
    const resumeWorkflow = () => {
      void tryResumeAuthWorkflow({
        uploadFile: runFileUpload,
        fetchUrl: runUrlFetch,
        pastePreview: runPastePreview,
        convertHost: runConvert,
      });
    };

    resumeWorkflow();
    window.addEventListener("auth-session-updated", resumeWorkflow);
    return () => window.removeEventListener("auth-session-updated", resumeWorkflow);
  }, []);

  // Preview is free \u2014 no sign-in wall. Sign-in is only required at Convert step.
  const handleFile = async (file: File) => {
    await runFileUpload(file);
  };

  const handleSampleSelect = async (specUrl: string) => {
    setLoadingSampleUrl(specUrl);
    try {
      await runUrlFetch(specUrl);
    } finally {
      setLoadingSampleUrl(null);
    }
  };

  const handleStartOver = () => {
    clearConversionWorkspace();
    setParsedRawData(null);
    setUploadedFile(null);
    setFetchedSpecUrl(null);
    setPastedSpecText(null);
    setLastConvertSuccess(false);
    setConnectSetup(null);
    setHostedMcpDisplayUrl(null);
    setDetectedAuthRequirement(null);
    setCredentials({});
    setCredentialsSkipped(false);
    setBaseUrlOverride("");
    setAuthOverride(null);
    setUseApiKeyInsteadOfOAuth(false);
    setUploadError(null);
  };

  const handleUrlFetch = async (url: string) => {
    await runUrlFetch(url);
  };

  const handleMergeUrls = async (urls: string[]) => {
    clearConversionWorkspace();
    try {
      setUploadError(null);
      setIsUploading(true);
      setLastConvertSuccess(false);
      setParsedRawData(null);
      setConnectSetup(null);
      setHostedMcpDisplayUrl(null);
      setUploadedFile(null);
      setFetchedSpecUrl(null);
      setCredentials({});
      setCredentialsSkipped(false);
      setBaseUrlOverride("");
      setAuthOverride(null);
      setUseApiKeyInsteadOfOAuth(false);
      setDetectedAuthRequirement(null);
      const mergedJson = await mergeSpecsFromUrls(urls);
      setPastedSpecText(mergedJson);
      const data = await uploadOpenAPIFromText(mergedJson);
      applyPreviewResult(data, setParsedRawData, setUploadError);
      trackSpecInput("url", undefined);
    } catch (err: unknown) {
      setPastedSpecText(null);
      setUploadError(toErrorMessage(err, "Merge failed"));
    } finally {
      setIsUploading(false);
    }
  };

  const handlePastedTextPreview = async (text: string) => {
    await runPastePreview(text);
  };

  const buildConvertHostIntent = async (): Promise<ConvertHostIntent | undefined> => {
    const credsPayload =
      Object.keys(credentials).length > 0
        ? (credentials as Record<string, string>)
        : undefined;
    const basePayload = baseUrlOverride.trim() || undefined;

    if (pastedSpecText) {
      return {
        kind: "convert-host",
        source: "paste",
        text: pastedSpecText,
        credentials: credsPayload,
        baseUrlOverride: basePayload,
      };
    }
    if (fetchedSpecUrl) {
      return {
        kind: "convert-host",
        source: "url",
        url: fetchedSpecUrl,
        credentials: credsPayload,
        baseUrlOverride: basePayload,
      };
    }
    if (uploadedFile) {
      const fileIntent = await fileToUploadIntent(uploadedFile);
      if (fileIntent.kind !== "upload-file") return undefined;
      return {
        kind: "convert-host",
        source: "file",
        fileName: fileIntent.fileName,
        fileType: fileIntent.fileType,
        dataBase64: fileIntent.dataBase64,
        credentials: credsPayload,
        baseUrlOverride: basePayload,
      };
    }
    return undefined;
  };

  const handleConvert = async () => {
    await requireSignedIn(
      () => runConvert(),
      () => buildConvertHostIntent(),
    );
  };

  return (
    <div className="min-h-screen bg-background relative">
      <GlobalBackground />
      <div className="relative" style={{ zIndex: 1 }}>
      <TopHeader />
      <HeroSection />
      <FormatsSection />
      <Reveal>
        <InputSection
          onFileSelected={handleFile}
          onUrlFetch={handleUrlFetch}
          onMergeUrls={handleMergeUrls}
          onPastedTextPreview={handlePastedTextPreview}
          isUploading={isUploading}
        />
      </Reveal>

      {uploadError && (
        <p className="container max-w-2xl mx-auto px-6 text-sm text-red-500">
          {uploadError}
        </p>
      )}

      <ProcessingUI isUploading={isUploading} isConverting={isConverting} />

      {/* ── Sample specs — shown in empty state (no spec loaded yet) ── */}
      {!parsedRawData && !isUploading && (
        <SampleSpecs
          onSelect={handleSampleSelect}
          loadingUrl={loadingSampleUrl}
        />
      )}

      {parsedRawData && (
        <div className="relative">
          {!lastConvertSuccess && (
            <div className="container max-w-2xl mx-auto px-6 mt-4 mb-3 flex justify-end">
              <button
                type="button"
                onClick={handleStartOver}
                aria-label="Clear spec and start again"
                className="w-8 h-8 flex items-center justify-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-colors"
              >
                <X size={16} aria-hidden />
              </button>
            </div>
          )}
        <PreviewPage
          parsedData={parsedData}
          onGenerate={handleConvert}
          baseUrlOverride={baseUrlOverride}
          onBaseUrlOverrideChange={setBaseUrlOverride}
          needsBaseUrlInput={showBaseUrlInput}
          authSchemes={authSchemesForSetup as never[]}
          credentials={credentials}
          onCredentialsChange={setCredentials}
          canSubmitSetup={baseUrlReady && authReady}
          isConverting={isConverting}
          hideInlineSetup
          showPostPreviewAuthSetup={showSetupSection}
          showBaseUrlStep={authSetupUi.showBaseUrlStep}
          showAuthOverride={showAuthOverrideOnPage}
          showAuthConfig={showAuthConfigOnPage}
          hasOAuthSupport={hasOAuthSupport}
          oauthConfig={oauthConfig}
          oauthConnected={oauthConnected}
          onOAuthSuccess={(encryptedToken: string) => {
            setCredentials(prev => ({
              ...prev,
              oauth_access_token_enc: encryptedToken,
            }));
            setCredentialsSkipped(false);
            setUseApiKeyInsteadOfOAuth(false);
          }}
          onOAuthDisconnect={() => {
            setCredentials(prev => {
              const next = { ...prev };
              delete next.oauth_access_token_enc;
              return next;
            });
          }}
          showGenerateCard={authSetupUi.showGenerateCard}
          overrideReason={overrideReason}
          authOverride={authOverride}
          credentialsSkipped={credentialsSkipped}
          onCredentialsSkip={() => {
            setCredentialsSkipped(true);
            setCredentials({});
          }}
          onCredentialsResume={() => setCredentialsSkipped(false)}
          onAuthOverrideChange={(mode: OverrideMode) => {
            setAuthOverride(mode);
            setCredentials({});
            setCredentialsSkipped(false);
            setUseApiKeyInsteadOfOAuth(false);
          }}
          baseUrlReady={baseUrlReady}
          authReady={authReady}
          needsManualOverride={needsManualOverride}
          authRequirement={
            parsedRawData.auth_requirement?.is_required ? parsedRawData.auth_requirement : null
          }
          authBannerConnected={oauthConnected || (
            !credentialsSkipped &&
            Object.keys(credentials).length > 0 &&
            credentialsComplete(authSchemesForSetup, credentials)
          )}
          onAuthBannerConnect={() => {
            if (hasOAuthSupport) return;
            setUploadError(
              "OAuth authorize/token URLs are missing from this spec. Use Skip, or pick API key / Bearer below if the API supports it.",
            );
          }}
          onAuthBannerSkip={() => {
            setCredentialsSkipped(true);
            setUseApiKeyInsteadOfOAuth(false);
          }}
          onAuthBannerUseApiKey={() => {
            setUseApiKeyInsteadOfOAuth(true);
            setCredentialsSkipped(false);
          }}
        />
        </div>
      )}

      {parsedRawData && !lastConvertSuccess && parsedRawData.workflows && parsedRawData.workflows.length > 0 && (
        <div className="container max-w-2xl mx-auto px-6">
          <WorkflowSuggestions workflows={parsedRawData.workflows} />
        </div>
      )}

      {parsedRawData && lastConvertSuccess && (
        <ConvertAction
          onConvert={handleConvert}
          isConverting={isConverting}
          lastConvertSuccess={lastConvertSuccess}
          mcpUrl={hostedMcpDisplayUrl}
          isPro={isProPlan}
          credentialsReady={true}
        />
      )}

      {/* ── Auth Detection Banner — shown after hosting if API needs credentials ── */}
      {lastConvertSuccess && detectedAuthRequirement && (
        <div className="container max-w-2xl mx-auto px-6 -mt-2 mb-2" id="auth-setup-section">
          <AuthDetectionBanner
            authRequirement={detectedAuthRequirement}
            isConnected={
              !credentialsSkipped &&
              Object.keys(credentials).length > 0 &&
              credentialsComplete(authSchemesForSetup, credentials)
            }
            onConnect={() => {
              const el = document.getElementById("auth-setup-section");
              if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
            }}
            onSkip={() => setCredentialsSkipped(true)}
          />
        </div>
      )}

      {connectSetup && lastConvertSuccess && (
        <section className="pb-6">
          <ConnectPage
            specTitle={connectSetup.specTitle}
            baseUrl={connectSetup.baseUrl}
            serverPath={connectSetup.serverPath}
            mcpUrl={connectSetup.mcpUrl}
            sseUrl={connectSetup.sseUrl}
          />
        </section>
      )}
      <Reveal><ActionButtons /></Reveal>
      <Reveal><PopularMcps /></Reveal>
      <Reveal><LiveStats /></Reveal>
      <Reveal><Testimonials /></Reveal>
      <Reveal><HowItWorks /></Reveal>
      <Footer />
      </div>
    </div>
  );
};

export default Index;